import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { connect } from 'react-redux';
import { bindActionCreators, compose } from 'redux';
import classNames from 'classnames';
import { Trans } from 'react-i18next';
import * as Sentry from '@sentry/react';
import i18n from '../../i18n';
import UserApi from '../../api/User';
import Common from '../../api/Common';
import InputImages from '../../../src/components/widget/InputImages';
import User from '../../models/User';
import TelInput from '../../../src/components/widget/TelInput';
import HandleErrors from '../../utils/HandleErrors';
import { withCurrency } from '../currency';
import { refreshHomes } from '../../actions/homes';
import withAnalytics from '../analytics/withAnalytics';
import Analytics from '../../utils/analytics';
import useGTM from '../analytics/useGTM';
import CollectionFunnelEvents from '../../utils/collectionFunnelEvents';

const SignupCollection = (props) => {
    const navigate = useNavigate();
    const [state, setState] = useState({ password: true, confirmPassword: true, hasZeroBounceError: false });
    const { register, handleSubmit, errors, getValues, control, setError, formState } = useForm();

    const onSubmit = (data) => {
        setState({ ...state, hasZeroBounceError: false });
        if (!_.isEmpty(data)) {
            props.getSending(true);
            UserApi.create(
                Object.assign({}, data, {
                    locale: i18n.language,
                    currency: props.currency
                }),
                true
            )
                .then((userCreated) => {
                    const { fetchUserCreated } = useGTM(new User(userCreated));

                    props.track('HEC Account created', { category: 'HEC application' });
                    Analytics.trackGTM('UserCreated', fetchUserCreated(null, 'email'));
                    Common.login(data.email, data.password, i18n.language)
                        .then(() => {
                            CollectionFunnelEvents.funnelStart();
                            props.refreshHomes();
                            navigate(location.pathname.replace(/[^/]*$/, 'step2'));
                        })
                        .catch(Sentry.captureException);
                    CollectionFunnelEvents.funnelStart();
                })
                .catch((error) => {
                    props.getSending(false);

                    if (error.responseJSON.errors === 'zero-bounce.email.error') {
                        setState({ ...state, hasZeroBounceError: true });
                        return;
                    }

                    const errorsEmail = error.responseJSON.errors.form.children.email.errors[0];
                    setError(
                        'email',
                        'focus',
                        errorsEmail === 'account_creation_failed'
                            ? 'account_creation_failed'
                            : i18n.t(`common:${errorsEmail}`)
                    );
                    if (errorsEmail === 'account_creation_failed') {
                        props.getEmail(data.email);
                    }
                });
        }
    };

    useEffect(() => {
        props.hasError(formState.dirtyFields.size !== 6);
    }, [formState]);

    const handleAlreadyHome = useCallback(() => {
        document.dispatchEvent(new CustomEvent('open_signin_popup'));
    }, []);

    const logIn = <a className="already-member" onClick={handleAlreadyHome} />;

    return (
        <form id="form-step-1" onSubmit={handleSubmit(onSubmit)}>
            <h2 className="title-signup">{i18n.t('collection:tell_us')}</h2>
            <div className="container-form-group">
                <div className="form-group log-in-text">
                    <Trans i18nKey="collection:already-account" components={[logIn]} i18n={i18n} />
                </div>
                <div className="form-group">
                    <div className="form-label">
                        <label htmlFor="firstname">{i18n.t('user:firstname')}</label>
                    </div>
                    <InputImages
                        classContainer={classNames('form-entity', {
                            'form-entity-error': !_.isEmpty(errors.first_name)
                        })}
                        classImageLeft="form-entity-icon"
                        imageLeft={<i className="icon-creme_first_name icon" />}
                    >
                        <input
                            id="firstname"
                            name="first_name"
                            ref={register({ required: i18n.t('common:first_name_null') })}
                            placeholder={i18n.t('collection:first_name_placeholder')}
                        />
                    </InputImages>
                    {!_.isEmpty(errors.first_name) &&
                        HandleErrors.renderErrors(errors.first_name, errors.first_name.message)}
                </div>
                <div className="form-group">
                    <div className="form-label">
                        <label htmlFor="lastname">{i18n.t('user:lastname')}</label>
                    </div>
                    <InputImages
                        classContainer={classNames('form-entity', {
                            'form-entity-error': !_.isEmpty(errors.last_name)
                        })}
                        classImageLeft="form-entity-icon"
                        imageLeft={<i className="icon-creme_first_name icon" />}
                    >
                        <input
                            id="lastname"
                            name="last_name"
                            ref={register({ required: i18n.t('common:last_name_null') })}
                            placeholder={i18n.t('collection:last_name_placeholder')}
                        />
                    </InputImages>
                    {!_.isEmpty(errors.last_name) &&
                        HandleErrors.renderErrors(errors.last_name, errors.last_name.message)}
                </div>
                <div className="form-group">
                    <div className="form-label">
                        <label htmlFor="email">{i18n.t('collection:email_address')}</label>
                        <p>{i18n.t('collection:to_keep_updated')}</p>
                    </div>
                    <InputImages
                        classContainer={classNames('form-entity', {
                            'form-entity-error': !_.isEmpty(errors.email)
                        })}
                        classImageLeft="form-entity-icon"
                        imageLeft={<i className="icon-creme_email icon" />}
                    >
                        <input
                            id="email"
                            name="email"
                            ref={register({
                                required: i18n.t('common:email_null'),
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: i18n.t('common:email_invalid')
                                }
                            })}
                            placeholder={i18n.t('collection:email_placeholder')}
                        />
                    </InputImages>
                    {state.hasZeroBounceError && (
                        <div className="form-errors">
                            <span role="alert">
                                <i className="icon-alert_red" />
                                <span
                                    dangerouslySetInnerHTML={{
                                        __html: i18n.t('subscription:zero-bounce.email.error')
                                    }}
                                />
                            </span>
                        </div>
                    )}
                    {!_.isEmpty(errors.email) &&
                        HandleErrors.renderErrors(
                            errors.email,
                            errors.email.message,
                            errors.email.message === 'account_creation_failed' ? logIn : false
                        )}
                </div>
                <div className="form-group form-tel-input">
                    <div className="form-label">
                        <label htmlFor="phone">{i18n.t('collection:phone_number')}</label>
                        <p>{i18n.t('collection:in_case_more_info')}</p>
                    </div>
                    <Controller
                        as={<TelInput hasErrors={!_.isEmpty(errors.phone)} signupCollection={true} />}
                        name="phone"
                        id="phone"
                        rules={{ required: i18n.t('common:phone_number_null') }}
                        control={control}
                    />
                    {!_.isEmpty(errors.phone) &&
                        HandleErrors.renderErrors(errors.phone, errors.phone.message)}
                </div>
                <div className="form-group">
                    <div className="form-label">
                        <label htmlFor="password">{i18n.t('collection:your_password')}</label>
                        <p>{i18n.t('collection:to_login')}</p>
                    </div>
                    <InputImages
                        classContainer={classNames('form-entity', {
                            'form-entity-error': !_.isEmpty(errors.password)
                        })}
                        classImageLeft="form-entity-icon"
                        classImageRight="form-entity-icon-right"
                        imageLeft={<i className="icon-creme_password icon" />}
                        imageRight={
                            <i
                                className={classNames('icon-creme_eye_password', {
                                    icon_opacity: !state.password
                                })}
                                onClick={() => setState({ ...state, password: !state.password })}
                            />
                        }
                    >
                        <input
                            id="password"
                            name="password"
                            ref={register({
                                required: i18n.t('common:password_null'),
                                validate: (value) =>
                                    User.USER_PWD_POLICY.test(value) || i18n.t('common:password_policy')
                            })}
                            type={state.password ? 'password' : 'text'}
                        />
                    </InputImages>
                    {!_.isEmpty(errors.password) &&
                        HandleErrors.renderErrors(errors.password, errors.password.message)}
                </div>
                <div className="form-group">
                    <div className="form-label">
                        <label htmlFor="confirmPassword">{i18n.t('collection:confirm_password')}</label>
                    </div>
                    <InputImages
                        classContainer={classNames('form-entity', {
                            'form-entity-error': !_.isEmpty(errors.confirmPassword)
                        })}
                        classImageLeft="form-entity-icon"
                        classImageRight="form-entity-icon-right"
                        imageLeft={<i className="icon-creme_password icon" />}
                        imageRight={
                            <i
                                className={classNames('icon-creme_eye_password', {
                                    icon_opacity: !state.confirmPassword
                                })}
                                onClick={() =>
                                    setState({
                                        ...state,
                                        confirmPassword: !state.confirmPassword
                                    })
                                }
                            />
                        }
                    >
                        <input
                            id="confirmPassword"
                            name="confirmPassword"
                            ref={register({
                                required: i18n.t('common:confirm_password_null'),
                                validate: (value) =>
                                    getValues('password') === value ||
                                    i18n.t('common:confirm_password_policy')
                            })}
                            type={state.confirmPassword ? 'password' : 'text'}
                        />
                    </InputImages>
                    {!_.isEmpty(errors.confirmPassword) &&
                        HandleErrors.renderErrors(errors.confirmPassword, errors.confirmPassword.message)}
                </div>
                <div className="form-group consent-form">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: i18n.t('collection:consent-form-collection')
                        }}
                    />
                </div>
            </div>
        </form>
    );
};

SignupCollection.propTypes = {
    getSending: PropTypes.func,
    currency: PropTypes.string,
    hasError: PropTypes.func,
    refreshHomes: PropTypes.func,
    track: PropTypes.func,
    getEmail: PropTypes.func
};

const mapDispatchToProps = (dispatch) => ({
    refreshHomes: bindActionCreators(refreshHomes, dispatch)
});

export default compose(withCurrency, connect(null, mapDispatchToProps), withAnalytics)(SignupCollection);
